import { useQuery } from '@apollo/client';
import { graphql } from '@gql/gql';
import type {
  ProWorkerGetAvailabilitiesQuery,
  ProWorkerGetAvailabilitiesQueryVariables,
} from '@gql/graphql';
import type { Dayjs } from 'dayjs';
import { useMemo } from 'react';

export const PRO_BOOKING_AVAILABILITIES = graphql(`
  query ProWorkerGetAvailabilities(
    $fromDate: DateTime!
    $proId: Int!
    $workerServiceId: Int
    $proServiceId: String
    $forMultiAnimals: Boolean!
  ) {
    findBookingAvailabilities(
      workerServiceId: $workerServiceId
      fromDate: $fromDate
      proId: $proId
      proServiceId: $proServiceId
      forMultiAnimals: $forMultiAnimals
    ) {
      mergedWorkerAvailabilities {
        startDateTime
        endDateTime
        workerServiceIds
        workerIds
      }
      nextAvailability {
        workerId
        date
      }
    }
  }
`);

export interface UseBookingAvailabilitiesParams {
  workerServiceId?: number;
  proId?: number;
  serviceId?: string;
  fromDate?: Dayjs;
  anyWorker: boolean;
  forMultiAnimals: boolean;
}

export const useBookingAvailabilities = (params: UseBookingAvailabilitiesParams) => {
  const { workerServiceId, proId, fromDate, serviceId, anyWorker, forMultiAnimals } = params;

  const skip = useMemo(() => {
    if (proId == null) return true;
    if (fromDate == null) return true;
    if (serviceId == null) return true;
    if (!anyWorker && workerServiceId == null) return true;
    return false;
  }, [anyWorker, fromDate, proId, serviceId, workerServiceId]);

  const variables = useMemo((): ProWorkerGetAvailabilitiesQueryVariables | undefined => {
    if (skip) return undefined;
    const vars: ProWorkerGetAvailabilitiesQueryVariables = {
      fromDate: fromDate?.toISOString() as string,
      proId: proId as number,
      forMultiAnimals,
    };
    if (anyWorker) {
      vars.proServiceId = serviceId;
    } else {
      vars.workerServiceId = workerServiceId;
    }
    return vars;
  }, [skip, fromDate, proId, anyWorker, serviceId, workerServiceId, forMultiAnimals]);

  const { data, error, loading } = useQuery<
    ProWorkerGetAvailabilitiesQuery,
    ProWorkerGetAvailabilitiesQueryVariables
  >(PRO_BOOKING_AVAILABILITIES, {
    variables,
    skip,
    fetchPolicy: 'network-only',
    ssr: false,
  });

  return {
    availabilities: data?.findBookingAvailabilities?.mergedWorkerAvailabilities || [],
    nextAvailability: skip ? undefined : data?.findBookingAvailabilities?.nextAvailability,
    availabilitiesError: error,
    availabilitiesLoading: loading,
  };
};
